// Single shipment detail. Live tracking timeline (auto-refresh 60s),
// audit log, and admin/pharmacist cancel-and-refund.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useTenantHref } from '../lib/tenantPath.jsx';

export default function ShipmentDetail() {
  const tHref = useTenantHref();
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setData(await api.shipment(id)); setErr(null); }
    catch (e) { setErr(e.body?.message || e.message); }
  }
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [id]);

  async function cancel() {
    if (!confirm('Cancel this shipment and refund the label?')) return;
    setBusy(true); setErr(null);
    try { await api.shipmentCancel(id); await load(); }
    catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }

  if (!data) return <div className="text-slate-500">{err || 'Loading…'}</div>;
  const s = data.shipment;
  const canCancel = (user?.role === 'admin' || user?.role === 'pharmacist')
                    && s.status !== 'canceled' && s.status !== 'delivered';

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Shipment</h1>
          <div className="text-sm text-slate-500">
            <Link to={tHref(`/patients/${s.patient_id}`)} className="hover:underline" style={{ color: 'var(--brand-primary)' }}>
              {s.first_name} {s.last_name}
            </Link>
            {' · '}<span className="font-mono text-xs">{s.mrn}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={tHref('/shipments')} className="btn-ghost">← All</Link>
          {canCancel && <button className="btn-ghost text-red-700" onClick={cancel} disabled={busy}>Cancel & refund</button>}
        </div>
      </div>

      {err && <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">{err}</div>}

      <div className="card p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge s={s.status} />
          {s.tracking_status_raw && <span className="text-xs text-slate-500">EasyPost: {s.tracking_status_raw}</span>}
          <div className="ml-auto text-sm text-slate-500">
            Created {new Date(s.created_at).toLocaleString()}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Reason" value={s.reason} />
          <Field label="Carrier / service" value={`${s.carrier || '—'}${s.service_level ? ` · ${s.service_level}` : ''}`} />
          <Field label="Tracking" value={s.tracking_number || '—'} mono />
          <Field label="Rate" value={s.rate_amount_cents != null ? `$${(s.rate_amount_cents / 100).toFixed(2)}` : '—'} mono />
          <Field label="Weight" value={s.weight_oz ? `${s.weight_oz} oz (${s.weight_source})` : '—'} />
          <Field label="PMS payment" value={s.pms_payment_ref ? `${s.pms_payment_ref} · $${Number(s.pms_payment_amount || 0).toFixed(2)}` : '— not yet recorded'} mono />
        </div>
        <div className="text-sm">
          <div className="text-xs uppercase tracking-wide text-slate-500 mt-2 mb-1">Ship to</div>
          <div className="font-mono whitespace-pre-line bg-slate-50 border border-slate-200 rounded p-2">
            {s.to_name}
            {'\n'}{s.to_street1}{s.to_street2 ? `, ${s.to_street2}` : ''}
            {'\n'}{s.to_city}, {s.to_state} {s.to_zip}
            {s.to_phone ? `\n${s.to_phone}` : ''}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          {s.label_url && (
            <a href={s.label_url} target="_blank" rel="noreferrer" className="btn-primary">Open label PDF</a>
          )}
          {s.tracking_number && (
            <a href={`https://track.easypost.com/?tn=${encodeURIComponent(s.tracking_number)}`}
               target="_blank" rel="noreferrer" className="btn-ghost">Public tracking page</a>
          )}
          {s.status !== 'label_purchased' && s.status !== 'in_transit' && s.status !== 'out_for_delivery'
            && s.status !== 'delivered' && s.status !== 'canceled' && (
            <button className="btn-ghost" onClick={() => nav(`/shipments/new?continue=${s.id}`)}>
              Continue this draft
            </button>
          )}
        </div>
      </div>

      {data.prescriptions?.length > 0 && (
        <div className="card p-4">
          <h2 className="font-semibold mb-2">Medications</h2>
          <ul className="divide-y divide-slate-100 text-sm">
            {data.prescriptions.map(rx => (
              <li key={rx.id} className="py-2 flex justify-between">
                <div>
                  <div className="font-medium">{rx.drug_name} {rx.strength}</div>
                  <div className="text-xs text-slate-500">
                    {rx.rx_number || '—'} · {rx.directions || ''}
                  </div>
                </div>
                <span className="badge-neutral">{rx.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.tracking?.events?.length > 0 && (
        <div className="card p-4">
          <h2 className="font-semibold mb-2">Tracking timeline</h2>
          <ol className="border-l-2 border-slate-200 ml-2 space-y-2">
            {data.tracking.events.map((e, i) => (
              <li key={i} className="ml-3 text-sm relative">
                <span className="absolute -left-[7px] w-2.5 h-2.5 rounded-full mt-1.5"
                      style={{ background: 'var(--brand-primary)' }} />
                <div className="font-medium">{e.status}</div>
                <div className="text-xs text-slate-500">
                  {new Date(e.datetime).toLocaleString()}
                  {e.location?.city && ` · ${e.location.city}, ${e.location.state || ''}`}
                </div>
                {e.message && <div className="text-xs text-slate-600">{e.message}</div>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {data.events?.length > 0 && (
        <div className="card p-4">
          <h2 className="font-semibold mb-2">Audit log</h2>
          <ol className="border-l-2 border-slate-200 ml-2 space-y-2">
            {data.events.map(ev => (
              <li key={ev.id} className="ml-3 text-sm relative">
                <span className="absolute -left-[7px] w-2.5 h-2.5 rounded-full mt-1.5 bg-slate-400" />
                <div className="font-medium">{ev.event_type}</div>
                <div className="text-xs text-slate-500">{new Date(ev.occurred_at).toLocaleString()}</div>
                {ev.payload && <div className="text-xs text-slate-600 font-mono">{JSON.stringify(ev.payload)}</div>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ s }) {
  const tone =
    s === 'delivered' ? 'bg-emerald-100 text-emerald-800'
    : s === 'in_transit' || s === 'out_for_delivery' || s === 'label_purchased' ? 'bg-blue-100 text-blue-800'
    : s === 'exception' || s === 'canceled' ? 'bg-red-100 text-red-800'
    : 'bg-slate-100 text-slate-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${tone}`}>{s}</span>;
}
function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono break-all' : ''}`}>{value}</div>
    </div>
  );
}
